import { Link } from 'react-router-dom'
import { motion, Variants } from 'framer-motion'
import { useState, useEffect } from 'react'

interface HeroProps {
  tagline?: string
  title: string
  subtitle?: string
  backgroundImage?: string
  primaryButtonText?: string
  primaryButtonLink?: string
  secondaryButtonText?: string
  secondaryButtonLink?: string
}

// Animation variants
const lineVariants: Variants = {
  hidden: { 
    opacity: 0,
    y: 8
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1]
    }
  }
}

const buttonVariants: Variants = {
  hidden: { 
    opacity: 0,
    y: 8
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1]
    }
  }
}


export default function Hero({ 
  tagline = "Una organización con causa:",
  title,
  subtitle,
  backgroundImage,
  primaryButtonText = "Apoya la Causa",
  primaryButtonLink = "/donar",
  secondaryButtonText = "Únete al Equipo",
  secondaryButtonLink = "/se-parte"
}: HeroProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)

  useEffect(() => {
    // Mark as animated after component mounts
    setHasAnimated(true)
  }, [])
  
  return (
    // Hero section container
    <div className="w-full flex flex-col gap-10 items-center justify-start">
      {/* Max width wrapper with padding */}
      <div className="max-w-7xl w-full">
        <div className="w-full flex flex-col gap-8 md:gap-12 items-start justify-start p-4 md:p-8">
          {/* Content section */}
          <div className="w-full">
            <div className="flex flex-col gap-8 items-start justify-start p-2 md:p-4">
              {/* Text content container */}
              <div className="flex flex-col gap-10 items-start justify-center max-w-5xl">
                {/* Heading group */}
                <motion.div 
                  className="flex flex-col gap-4 items-start justify-start w-full"
                  initial={!hasAnimated ? "hidden" : "visible"}
                  animate="visible"
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.06,
                        delayChildren: 0.1
                      }
                    }
                  }}
                >
                  {/* Tagline */}
                  {tagline && (
                    <motion.p 
                      className="font-sans text-gray-500 text-base md:text-xl tracking-tight"
                      variants={lineVariants}
                    >
                      {tagline}
                    </motion.p>
                  )}
                  {/* Main heading */}
                  <motion.h1 
                    className="font-serif font-normal text-black text-3xl md:text-5xl tracking-tight leading-tight break-words"
                    variants={lineVariants}
                  >
                    {title}
                  </motion.h1>
                  {subtitle && (
                    <motion.p 
                      className="font-serif text-gray-700 text-xl break-words"
                      variants={lineVariants}
                    >
                      {subtitle}
                    </motion.p>
                  )}
                </motion.div>
                {/* CTA buttons container */}
                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start sm:items-center justify-start sm:justify-center"
                  initial={!hasAnimated ? "hidden" : "visible"}
                  animate="visible"
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.08,
                        delayChildren: 0.3
                      }
                    }
                  }}
                >
                  {/* Primary button */}
                  <motion.div
                    variants={buttonVariants}
                    whileHover={{ backgroundColor: '#ff8533' }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="bg-[#ff6a00] rounded-full inline-block"
                  >
                    <Link 
                      to={primaryButtonLink}
                      className="text-white hover:text-white font-sans text-sm px-5 py-3 block"
                    >
                      {primaryButtonText}
                    </Link>
                  </motion.div>
                  {/* Secondary button with arrow */}
                  <motion.div
                    variants={buttonVariants}
                    whileHover={{ opacity: 0.7 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <Link 
                      to={secondaryButtonLink}
                      className="flex flex-row gap-2 items-center justify-center"
                    >
                      <span className="font-sans text-black text-sm tracking-tight">
                        {secondaryButtonText}
                      </span>
                      {/* Arrow icon */}
                      <svg 
                        className="w-4 h-4 text-black" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M9 5l7 7-7 7" 
                        />
                      </svg>
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
          {/* Hero image */}
          {backgroundImage && (
            <div className="relative w-full h-64 sm:h-96 md:h-[500px] lg:h-[800px] rounded-lg overflow-hidden">
              {/* Skeleton loader */}
              <motion.div 
                className="absolute inset-0 bg-gray-200 rounded-lg"
                initial={{ opacity: 1 }}
                animate={{ opacity: imageLoaded ? 0 : 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
              </motion.div>
              
              {/* Actual image */}
              <motion.img
                src={backgroundImage}
                alt="Hero image"
                className="absolute inset-0 w-full h-full object-cover rounded-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: imageLoaded ? 1 : 0 }}
                transition={{ duration: 0.4 }}
                onLoad={() => setImageLoaded(true)}
                loading="lazy"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 